import { max, min, parseISO, roundToNearestMinutes } from "date-fns";
import { millisecondsInMinute } from "date-fns/constants";

export interface GetEtaUpdateAvailableTimesParams {
  shiftStartTime: string;
  shiftEndTime: string;
  targetArrivalTime?: string;
  updatedEta?: string;
  etaInMinutes: number;
  now: Date;
  maxMinutesAheadToUpdateEta: number;
}

const INTERVAL_MINUTES = 15;

/**
 * Generates a list of selectable ETA times for a worker updating their estimated arrival time.
 *
 * Times are generated at 15-minute intervals, starting from the later of:
 * - The anchor time (updated ETA, target arrival time, or shift start time), rounded down to the nearest 15 minutes
 * - The HyperTrack-calculated ETA (if provided), rounded down to the nearest 15 minutes
 *
 * Times end at the maximum allowed ETA, which is the earlier of:
 * - The anchor time plus `maxMinutesAheadToUpdateEta`
 * - The shift end time minus 15 minutes
 *
 * @param params - The parameters for generating available ETA times
 * @param params.shiftStartTime - ISO 8601 timestamp of when the shift starts
 * @param params.targetArrivalTime - ISO 8601 timestamp of the worker's target arrival time (optional)
 * @param params.updatedEta - ISO 8601 timestamp of a previously updated ETA (optional, takes priority over targetArrivalTime)
 * @param params.etaInMinutes - HyperTrack-calculated ETA in minutes from now
 * @param params.now - The current date/time
 * @param params.maxMinutesAheadToUpdateEta - Maximum number of minutes ahead of the anchor time that can be selected
 * @returns An array of Date objects representing selectable ETA times at 15-minute intervals
 */
export function getEtaUpdateAvailableTimes(params: GetEtaUpdateAvailableTimesParams): Date[] {
  const {
    shiftStartTime,
    shiftEndTime,
    etaInMinutes,
    now,
    updatedEta,
    targetArrivalTime,
    maxMinutesAheadToUpdateEta,
  } = params;

  const anchorStartDate = parseISO(updatedEta ?? targetArrivalTime ?? shiftStartTime);
  const anchorClamped = roundToNearestMinutes(anchorStartDate, {
    nearestTo: INTERVAL_MINUTES,
    roundingMethod: "floor",
  });

  const htEtaRaw = new Date(now.getTime() + etaInMinutes * millisecondsInMinute);
  const htEtaRoundedDown = roundToNearestMinutes(htEtaRaw, {
    nearestTo: INTERVAL_MINUTES,
    roundingMethod: "floor",
  });

  const shiftStart = parseISO(shiftStartTime);
  const shiftEnd = parseISO(shiftEndTime);
  const shiftEndMinus15 = new Date(shiftEnd.getTime() - INTERVAL_MINUTES * millisecondsInMinute);

  const minEtaUnclamped = max([anchorClamped, htEtaRoundedDown]);
  // The shift-start ceiling prevents a prior updatedEta past shift start from
  // ratcheting the window forward on subsequent opens.
  const maxEta = min([
    new Date(anchorClamped.getTime() + maxMinutesAheadToUpdateEta * millisecondsInMinute),
    new Date(shiftStart.getTime() + maxMinutesAheadToUpdateEta * millisecondsInMinute),
    shiftEndMinus15,
  ]);
  const minEta = min([minEtaUnclamped, maxEta]);

  const times: Date[] = [];
  for (
    let cursor = minEta;
    cursor <= maxEta;
    cursor = new Date(cursor.getTime() + INTERVAL_MINUTES * millisecondsInMinute)
  ) {
    times.push(cursor);
  }

  return times;
}
